import * as React from "react";

type ClassValue = string | number | boolean | null | undefined;
function cn(...inputs: ClassValue[]): string {
  return inputs.filter(Boolean).join(" ");
}

// --- Icons ---
const PlusIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M12 5V19" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 12H19" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const SendIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M12 5.25L12 18.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.75 12L12 5.25L5.25 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const XIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * Minimal prompt box: a growing textarea with just an upload (+) button and a
 * send button. Warm Claude-style surface. No tooltips, popovers or dialogs, so
 * it renders cleanly inside the widget's Shadow DOM.
 */
export const PromptBox = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => {
    const internalTextareaRef = React.useRef<HTMLTextAreaElement>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [value, setValue] = React.useState("");
    const [imagePreview, setImagePreview] = React.useState<string | null>(null);

    React.useImperativeHandle(ref, () => internalTextareaRef.current!, []);

    React.useLayoutEffect(() => {
      const textarea = internalTextareaRef.current;
      if (textarea) {
        textarea.style.height = "auto";
        const newHeight = Math.min(textarea.scrollHeight, 200);
        textarea.style.height = `${newHeight}px`;
      }
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
      if (props.onChange) props.onChange(e);
    };
    const handlePlusClick = () => fileInputRef.current?.click();
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file && file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result as string);
        reader.readAsDataURL(file);
      }
      event.target.value = "";
    };
    const handleRemoveImage = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const hasValue = value.trim().length > 0 || !!imagePreview;

    return (
      <div
        className={cn(
          "flex flex-col rounded-[24px] p-2 bg-white border border-[#E6DECB] shadow-sm transition-colors focus-within:border-[#D2785A]/60 cursor-text",
          className,
        )}
      >
        <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />

        {imagePreview && (
          <div className="relative mb-1 ml-1 w-fit pt-1">
            <img src={imagePreview} alt="Attached preview" className="h-16 w-16 rounded-[14px] object-cover" />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute -right-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-[#2B2A28] text-white transition-transform hover:scale-105"
              aria-label="Remove image"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <textarea
          ref={internalTextareaRef}
          rows={1}
          value={value}
          onChange={handleInputChange}
          placeholder="Ask me about sleep or mattresses..."
          className="custom-scrollbar w-full resize-none border-0 bg-transparent px-3 py-2.5 text-[15px] text-[#2B2A28] placeholder:text-[#2B2A28]/40 focus:ring-0 focus-visible:outline-none min-h-11"
          {...props}
        />

        <div className="mt-0.5 flex items-center px-1 pb-0.5">
          <button
            type="button"
            onClick={handlePlusClick}
            title="Attach image"
            aria-label="Attach image"
            className="flex h-9 w-9 items-center justify-center rounded-full text-[#2B2A28]/70 transition-colors hover:bg-[#F1ECE0] hover:text-[#2B2A28] focus-visible:outline-none"
          >
            <PlusIcon className="h-6 w-6" />
          </button>

          <button
            type="submit"
            disabled={!hasValue}
            title="Send"
            aria-label="Send message"
            className="ml-auto flex h-9 w-9 items-center justify-center rounded-full transition-all focus-visible:outline-none disabled:cursor-not-allowed bg-[#D2785A] text-white hover:bg-[#c2674a] disabled:bg-[#E4DDCD] disabled:text-[#2B2A28]/30"
          >
            <SendIcon className="h-[22px] w-[22px]" />
          </button>
        </div>
      </div>
    );
  },
);
PromptBox.displayName = "PromptBox";
